import { Grip } from 'lucide-react'
import PropTypes from 'prop-types'
import { useCallback, useState } from 'react'
import { useDrag, useDrop } from 'react-dnd'
import { useFieldArray, useFormContext } from 'react-hook-form'

import { Button } from '@/components/elements/button'
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/elements/form'
import { Input } from '@/components/elements/input'
import { Separator } from '@/components/elements/separator'
import { Table, TableBody, TableCell, TableRow } from '@/components/elements/table'

import QuizQuestion from './QuizQuestion'

const ContentQuiz = ({ name, readOnly }) => {
  // State
  const [showQuizQuestion, setShowQuizQuestion] = useState(false)
  const [editQuestionIndex, setEditQuestionIndex] = useState(null)

  // Form
  const { control, getValues } = useFormContext()
  const {
    fields: questions,
    append,
    move,
    remove,
    update,
  } = useFieldArray({
    control,
    name: `${name}.quiz.questions`,
  })

  const orderQuestions = useCallback(
    (dragIndex, hoverIndex) => move(dragIndex, hoverIndex),
    [move],
  )
  const renderQuestion = useCallback(
    (question, questionIndex) => (
      <SimpleQuizQuestion
        question={question}
        key={question.id}
        editQuestion={() => {
          setEditQuestionIndex(questionIndex)
          setShowQuizQuestion(true)
        }}
        index={questionIndex}
        orderQuestions={orderQuestions}
        readOnly={readOnly}
        remove={remove}
      />
    ),
    [orderQuestions, readOnly, remove],
  )

  return (
    <div key={`${name}.quiz`} className="flex flex-col gap-4">
      <div className="flex w-full flex-col gap-4">
        <span className="text-md font-semibold">Quiz</span>

        <FormField
          control={control}
          name={`${name}.quiz.numQuestionsToDisplay`}
          render={({ field: quizField }) => (
            <FormItem>
              <FormLabel>Number of Questions to Display</FormLabel>
              <FormDescription className="text-muted-foreground text-sm">
                Number of questions that will be displayed to the student. You can configure more
                questions than this.
              </FormDescription>
              <FormControl>
                <Input
                  id="numQuestionsToDisplay"
                  type="number"
                  min="1"
                  max={questions.length}
                  {...quizField}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
          rules={{
            validate: (value) => {
              if (value > questions.length)
                return 'Number of questions to display cannot exceed total number of questions.'
              if (value < 1) return 'Number of questions to display must be at least 1.'
              return true
            },
          }}
        />

        <FormField
          control={control}
          name={`${name}.quiz.numQuestionsToPass`}
          render={({ field: quizField }) => (
            <FormItem>
              <FormLabel>Number of Questions to Pass</FormLabel>
              <FormDescription className="text-muted-foreground text-sm">
                Number of questions a student must answer correctly to pass the quiz.
              </FormDescription>
              <FormControl>
                <Input
                  id="numQuestionsToPass"
                  type="number"
                  min="1"
                  max={questions.length}
                  {...quizField}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
          rules={{
            validate: (value) => {
              if (value > questions.length)
                return 'Number of questions to pass cannot exceed total number of questions.'
              if (value < 1) return 'Number of questions to pass must be at least 1.'
              return true
            },
          }}
        />

        <div className="flex flex-col">
          <FormLabel>Questions</FormLabel>
          <Separator className="mt-2" />
          {questions.length > 0 ? (
            <Table className="w-full">
              <TableBody>
                {questions.map((question, questionIndex) =>
                  renderQuestion(question, questionIndex),
                )}
              </TableBody>
            </Table>
          ) : (
            <span className="text-muted-foreground mt-2 self-center text-sm">
              No questions added yet.
            </span>
          )}

          {!readOnly && (
            <Button
              className="self-end"
              type="button"
              variant="outline"
              onClick={() => setShowQuizQuestion(true)}
            >
              Add Question
            </Button>
          )}
        </div>
      </div>

      {showQuizQuestion && (
        <QuizQuestion
          append={append}
          close={() => {
            setShowQuizQuestion(false)
            setEditQuestionIndex(null)
          }}
          // Read the stored value, not the field-array entry: useFieldArray
          // overwrites each item's `id` with its own render key, which hides
          // the question's database id. Handing the panel a field object cost
          // the id on save, so the backend deleted the question and recreated
          // it under a new uuid every time someone edited it (RSCX-385).
          question={
            editQuestionIndex !== null
              ? getValues(`${name}.quiz.questions.${editQuestionIndex}`)
              : null
          }
          index={editQuestionIndex}
          readOnly={readOnly}
          remove={remove}
          update={update}
        />
      )}
    </div>
  )
}

ContentQuiz.propTypes = {
  name: PropTypes.string.isRequired,
  readOnly: PropTypes.bool,
}

export default ContentQuiz

const SimpleQuizQuestion = ({
  question,
  index,
  orderQuestions,
  editQuestion,
  readOnly = false,
  remove,
}) => {
  // Drag & Drop
  const [{ opacity }, drop] = useDrop({
    accept: 'question',
    drop({ question: draggedQuestion, index: draggedIndex }) {
      if (draggedQuestion.id !== question.id) {
        orderQuestions(draggedIndex, index)
      }
    },
  })
  const [{ handlerId }, drag] = useDrag(
    () => ({
      type: 'question',
      item: { question, index },
      collect: (monitor) => ({
        opacity: monitor.isDragging() ? 0.5 : 1,
        isDragging: monitor.isDragging(),
      }),
    }),
    [question, index],
  )

  return (
    <TableRow
      data-handler-id={handlerId}
      ref={drop}
      key={question.id}
      style={{
        opacity,
        alignItems: 'center',
      }}
    >
      <TableCell ref={drag}>
        <Grip className="text-muted-foreground mx-auto size-6 cursor-move" />
      </TableCell>
      <TableCell className="flex w-full">
        {question.type === 'Drag and Drop' ? 'Drag and Drop' : question.text}
      </TableCell>
      <TableCell className="flex w-auto shrink-0 flex-row gap-2">
        <Button variant="outline" type="button" onClick={editQuestion}>
          Edit
        </Button>
        {!readOnly && (
          <Button variant="destructive" type="button" onClick={() => remove(index)}>
            Remove
          </Button>
        )}
      </TableCell>
    </TableRow>
  )
}

SimpleQuizQuestion.propTypes = {
  question: PropTypes.object.isRequired,
  editQuestion: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  orderQuestions: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  remove: PropTypes.func.isRequired,
}
